import styled from 'styled-components';
import type { FleetGroupUnit, GroupRate } from '@shared/api';
import { pluralizeRu } from '@shared/lib/formatFile';
import { SkeletonLine } from '@shared/ui/Skeleton';
import {
  FleetChip,
  Notice,
  TextButton,
  basisText,
  fleetLeading,
  fleetType,
  formatDateTime,
  formatIsoDate,
  formatMoney,
  periodText,
  rateInputsInfo,
  rateUnit,
  typo,
  type RateValue,
} from '@entities/fleet';
import type { RateTarget } from './scope';

const Context = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;

  & > strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
`;

const Timeline = styled.ol`
  position: relative;
  margin: 0;
  padding: 0;
  list-style: none;

  &::before {
    content: '';
    position: absolute;
    top: 8px;
    bottom: 8px;
    left: 4px;
    width: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
`;

const Entry = styled.li`
  position: relative;
  padding: ${({ theme }) => `0 0 ${theme.spacing(6)} ${theme.spacing(6)}`};

  &::before {
    content: '';
    position: absolute;
    top: 6px;
    left: 0;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.backgroundElevated};
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.border};
  }

  &[data-now='true']::before {
    background: ${({ theme }) => theme.colors.primary};
    box-shadow: none;
  }

  &:last-child {
    padding-bottom: 0;
  }
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.tight};

  & > .period {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Value = styled.p`
  margin: ${({ theme }) => `${theme.spacing(1.5)} 0 0`};
  font-size: ${fleetType.lead};
  font-weight: 600;
  font-variant-numeric: tabular-nums;

  & > .unit {
    margin-left: ${({ theme }) => theme.spacing(1.5)};
    font-size: ${fleetType.small};
    font-weight: 400;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const Meta = styled.p`
  margin: ${({ theme }) => `${theme.spacing(1)} 0 0`};
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
  overflow-wrap: anywhere;
`;

const Comment = styled.p`
  margin: ${({ theme }) => `${theme.spacing(1.5)} 0 0`};
  padding-left: ${({ theme }) => theme.spacing(3)};
  border-left: 2px solid ${({ theme }) => theme.colors.border};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  white-space: pre-wrap;
`;

const Muted = styled.p`
  margin: 0;
  font-size: ${fleetType.body};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** «вручную» или «по подсказке: среднее по 7 моделям, ПРЦ «…»». */
function sourceText(rate: GroupRate): string {
  if (rate.source !== 'suggestion') return 'вручную';
  const info = rateInputsInfo(rate.inputs);
  if (!info) return 'по подсказке';
  const pick = info.pick === 'weighted' ? 'средневзвешенное' : 'среднее';
  const models =
    info.modelsCount > 0
      ? ` по ${info.modelsCount} ${pluralizeRu(info.modelsCount, ['модели', 'моделям', 'моделям'])}`
      : '';
  const versions =
    info.versions.length > 0
      ? `, ${info.versions.length > 1 ? 'версии' : 'версия'} ${info.versions.map((v) => `«${v}»`).join(', ')}`
      : '';
  return `по подсказке: ${pick}${models}${versions}`;
}

interface RateHistoryProps {
  history: readonly GroupRate[] | undefined;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  /** Дата экрана — какая ставка на неё действует. */
  on: string;
  target: RateTarget;
  /** Ставка по умолчанию на ту же дату — для объекта. */
  fallback: RateValue | null;
  unit: FleetGroupUnit;
  /** Только что сохранённая ставка — отмечена. */
  savedId: string | null;
}

/** История ставок группы у объекта или по умолчанию: свежие сверху. */
export function RateHistory({
  history,
  loading,
  error,
  onRetry,
  on,
  target,
  fallback,
  unit,
  savedId,
}: RateHistoryProps) {
  const unitText = rateUnit(unit);
  const context =
    target.kind === 'project' ? (
      fallback ? (
        <Context>
          По умолчанию для холдинга на {formatIsoDate(on)}:{' '}
          <strong>
            {formatMoney(fallback.rate)} {unitText}
          </strong>
          {fallback.validFrom ? ` с ${formatIsoDate(fallback.validFrom)}` : ''}.{' '}
          {typo('Применяется, пока у объекта нет своей ставки.')}
        </Context>
      ) : (
        <Context>
          {typo(
            `Ставки по умолчанию для холдинга на ${formatIsoDate(on)} нет: без своей ставки объекта группа останется без денег в БДР.`,
          )}
        </Context>
      )
    ) : null;

  if (loading) {
    return (
      <div aria-busy="true" aria-label="Загрузка истории ставок">
        <SkeletonLine $width="40%" />
        <SkeletonLine $width="85%" />
        <SkeletonLine $width="65%" />
      </div>
    );
  }
  if (error) {
    return (
      <Notice $tone="danger" role="alert">
        <span className="text">{error}</span>
        <TextButton type="button" onClick={onRetry}>
          Повторить
        </TextButton>
      </Notice>
    );
  }
  const items = history ?? [];
  return (
    <>
      {context}
      {items.length === 0 ? (
        <Muted>
          {target.kind === 'project'
            ? 'Своих ставок для этой группы на объекте пока не задавали.'
            : 'Ставок по умолчанию для этой группы пока не задавали.'}
        </Muted>
      ) : (
        <Timeline aria-label="История ставок">
          {items.map((r) => {
            const now = r.valid_from <= on && (r.valid_to === null || r.valid_to >= on);
            const future = r.valid_from > on;
            return (
              <Entry key={r.id} data-now={now ? 'true' : undefined}>
                <Head>
                  <span className="period">{periodText(r.valid_from, r.valid_to)}</span>
                  {now && <FleetChip $tone="success">действует на {formatIsoDate(on)}</FleetChip>}
                  {future && <FleetChip $tone="info">вступит в силу</FleetChip>}
                  {r.id === savedId && <FleetChip $tone="neutral">сохранена только что</FleetChip>}
                </Head>
                <Value>
                  {formatMoney(r.rate)}
                  <span className="unit">{unitText} без НДС</span>
                </Value>
                <Meta>
                  База — {basisText(r.basis)} · {sourceText(r)}
                </Meta>
                <Meta>
                  Задано: {r.created_by_name ? `${r.created_by_name}, ` : ''}
                  {formatDateTime(r.created_at)}
                </Meta>
                {r.comment && <Comment>{r.comment}</Comment>}
              </Entry>
            );
          })}
        </Timeline>
      )}
    </>
  );
}
